<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  panel: {
    type: String,
    default: null
  },
  categories: {
    type: Array,
    default: () => []
  },
  serviceLinks: {
    type: Array,
    default: () => []
  },
  overLinks: {
    type: Array,
    default: () => []
  }
});

defineEmits(['close']);

const selectedCategoryId = ref(null);

const categoryList = computed(() =>
  (props.categories || []).filter((c) => c && typeof c.name === 'string')
);

const selectedCategory = computed(() => {
  const list = categoryList.value;
  if (!list.length) return null;
  const id = selectedCategoryId.value;
  return list.find((c) => String(c.category_id) === String(id)) || list[0];
});

watch(
  () => props.panel,
  (panel) => {
    if (panel === 'assortiment' && categoryList.value.length) {
      selectedCategoryId.value = categoryList.value[0].category_id;
    }
  },
  { immediate: true }
);

watch(categoryList, (list) => {
  if (list.length && selectedCategoryId.value == null) {
    selectedCategoryId.value = list[0].category_id;
  }
});

function selectCategory(cat) {
  selectedCategoryId.value = cat.category_id;
}

function childColumns(children, columnCount) {
  if (!children?.length) return [];
  const cols = Math.max(1, Number(columnCount) || 1);
  const perCol = Math.ceil(children.length / cols);
  const result = [];
  for (let i = 0; i < children.length; i += perCol) {
    result.push(children.slice(i, i + perCol));
  }
  return result;
}
</script>

<template>
  <div
    v-if="panel"
    class="vsb-mega"
    role="dialog"
    aria-modal="true"
    :aria-label="panel === 'assortiment' ? 'Assortiment' : panel === 'service' ? 'Service' : 'Over VSB'"
  >
    <div class="vsb-mega__inner">
      <!-- Assortiment: categorieën -->
      <div v-if="panel === 'assortiment'" class="vsb-mega__assortiment">
        <aside class="vsb-mega__sidebar">
          <p class="vsb-mega__sidebar-title">Categorieën</p>
          <ul class="vsb-mega__cat-list">
            <li v-for="cat in categoryList" :key="cat.category_id">
              <button
                type="button"
                class="vsb-mega__cat-btn"
                :class="{ 'vsb-mega__cat-btn--active': String(selectedCategory?.category_id) === String(cat.category_id) }"
                @mouseenter="selectCategory(cat)"
                @focus="selectCategory(cat)"
                @click="selectCategory(cat)"
              >
                {{ cat.name }}
              </button>
            </li>
          </ul>
        </aside>
        <div v-if="selectedCategory" class="vsb-mega__detail">
          <div class="vsb-mega__detail-head">
            <h3 class="vsb-mega__detail-title">{{ selectedCategory.name }}</h3>
            <a :href="selectedCategory.href" class="vsb-mega__view-all">Bekijk alles →</a>
          </div>
          <div v-if="selectedCategory.children?.length" class="vsb-mega__columns">
            <ul
              v-for="(col, ci) in childColumns(selectedCategory.children, selectedCategory.column)"
              :key="ci"
              class="vsb-mega__col"
            >
              <li v-for="child in col" :key="child.category_id" class="vsb-mega__child">
                <a :href="child.href" class="vsb-mega__child-link">{{ child.name }}</a>
                <ul v-if="child.children?.length" class="vsb-mega__grandchildren">
                  <li v-for="gc in child.children" :key="gc.category_id">
                    <a :href="gc.href">{{ gc.name }}</a>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <p v-else class="vsb-mega__empty">Geen subcategorieën</p>
        </div>
        <p v-else class="vsb-mega__empty vsb-mega__empty--full">Geen categorieën beschikbaar</p>
      </div>

      <!-- Service / Over VSB: linklijst -->
      <div v-else-if="panel === 'service' || panel === 'over'" class="vsb-mega__links">
        <h3 class="vsb-mega__links-title">
          {{ panel === 'service' ? 'Service' : 'Over VSB' }}
        </h3>
        <ul class="vsb-mega__link-grid">
          <li v-for="(link, i) in panel === 'service' ? serviceLinks : overLinks" :key="i">
            <a :href="link.href" class="vsb-mega__link-item">{{ link.name }}</a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.vsb-mega {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 10050;
  background: #fff;
  border-top: 1px solid #dce8f4;
  box-shadow: 0 16px 48px rgba(11, 61, 145, 0.12);
  max-height: min(72vh, 560px);
  overflow: hidden;
}

.vsb-mega__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 1.25rem 1.25rem 1.5rem;
  box-sizing: border-box;
  max-height: min(72vh, 560px);
  overflow: auto;
}

.vsb-mega__assortiment {
  display: grid;
  grid-template-columns: minmax(200px, 260px) 1fr;
  gap: 1.5rem;
  min-height: 200px;
}

.vsb-mega__sidebar-title,
.vsb-mega__links-title {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #5c6f82;
}

.vsb-mega__cat-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.vsb-mega__cat-btn {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0b3d91;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.vsb-mega__cat-btn:hover,
.vsb-mega__cat-btn--active {
  background: #eef4fb;
  color: #1456b8;
}

.vsb-mega__detail-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e8f0f8;
}

.vsb-mega__detail-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0b3d91;
}

.vsb-mega__view-all {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1456b8;
  text-decoration: none;
  white-space: nowrap;
}

.vsb-mega__view-all:hover {
  text-decoration: underline;
}

.vsb-mega__columns {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem 1.5rem;
}

.vsb-mega__col {
  list-style: none;
  margin: 0;
  padding: 0;
}

.vsb-mega__child {
  margin-bottom: 0.75rem;
}

.vsb-mega__child-link {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0b3d91;
  text-decoration: none;
  padding: 0.15rem 0;
}

.vsb-mega__child-link:hover {
  color: #1456b8;
}

.vsb-mega__grandchildren {
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0 0 0 0.5rem;
  border-left: 2px solid #e8f0f8;
}

.vsb-mega__grandchildren a {
  display: block;
  padding: 0.2rem 0;
  font-size: 0.8125rem;
  color: #5c6f82;
  text-decoration: none;
}

.vsb-mega__grandchildren a:hover {
  color: #1456b8;
}

.vsb-mega__link-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.5rem 1.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.vsb-mega__link-item {
  display: block;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0b3d91;
  text-decoration: none;
  background: #f7fafe;
  border: 1px solid #e8f0f8;
  transition: background 0.15s, border-color 0.15s;
}

.vsb-mega__link-item:hover {
  background: #eef4fb;
  border-color: #c5d9f0;
  color: #1456b8;
}

.vsb-mega__empty {
  margin: 0;
  color: #94a3b8;
  font-size: 0.9375rem;
}

.vsb-mega__empty--full {
  grid-column: 1 / -1;
  padding: 2rem;
  text-align: center;
}

@media (max-width: 768px) {
  .vsb-mega {
    display: none;
  }
}
</style>
